import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AffiliationRoleCheckboxes } from '@/components/contacts/affiliations/AffiliationRoleCheckboxes'
import { invalidateAffiliationQueries } from '@/components/contacts/affiliations/queryInvalidation'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-time-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { affiliationMutationMessage } from '@/lib/contacts/errors'
import type {
  OrganizationProfile,
  PersonOption,
  ProfileAffiliation,
  ProfilePerson,
} from '@/lib/contacts/types'

interface PersonsResponse {
  persons: PersonOption[]
}

interface AffiliationResponse {
  affiliation: ProfileAffiliation
}

export type OrganizationAffiliationDialogState =
  | { kind: 'add' }
  | { kind: 'edit'; person: ProfilePerson }

interface OrganizationAffiliationDialogProps {
  organization: OrganizationProfile
  dialog: OrganizationAffiliationDialogState
  onClose: () => void
}

export function OrganizationAffiliationDialog({
  organization,
  dialog,
  onClose,
}: OrganizationAffiliationDialogProps) {
  const queryClient = useQueryClient()
  const editing = dialog.kind === 'edit'
  const [personId, setPersonId] = React.useState('')
  const [roles, setRoles] = React.useState<string[]>(editing ? dialog.person.roles : [])
  const [startDate, setStartDate] = React.useState(editing ? (dialog.person.started_on ?? '') : '')
  const [message, setMessage] = React.useState<string | null>(null)
  const personsQuery = useQuery({
    queryKey: ['persons'],
    queryFn: () => getJSON<PersonsResponse>('/api/v1/persons'),
    enabled: !editing,
    retry: false,
  })
  const saveAffiliation = useMutation<AffiliationResponse, Error>({
    mutationFn: () => {
      if (editing) {
        return patchJSON<AffiliationResponse>(`/api/v1/affiliations/${dialog.person.id}`, {
          affiliation: { roles, started_on: startDate || null },
        })
      }

      return postJSON<AffiliationResponse>(
        `/api/v1/organizations/${organization.id}/affiliations`,
        {
          affiliation: { person_id: Number(personId), roles, started_on: startDate || null },
        },
      )
    },
    onSuccess: async () => {
      setMessage(null)
      await invalidateAffiliationQueries(queryClient, {
        personId: editing ? dialog.person.person_id : Number(personId),
        organizationId: organization.id,
      })
      onClose()
    },
    onError: (error) => setMessage(affiliationMutationMessage(error)),
  })
  const endAffiliation = useMutation<void, Error>({
    mutationFn: () => {
      if (!editing) throw new Error('No affiliation selected.')
      return deleteJSON(`/api/v1/affiliations/${dialog.person.id}`)
    },
    onSuccess: async () => {
      if (!editing) return

      setMessage(null)
      await invalidateAffiliationQueries(queryClient, {
        personId: dialog.person.person_id,
        organizationId: organization.id,
      })
      onClose()
    },
    onError: (error) => setMessage(affiliationMutationMessage(error)),
  })
  const targetName = editing ? dialog.person.name : 'a person'

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            saveAffiliation.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit person' : 'Add person'}</DialogTitle>
            <DialogDescription>
              Affiliate {targetName} with {organization.name} and choose their roles there.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {message && <Alert>{message}</Alert>}
            {!editing && (
              <AffiliationPersonSelect
                persons={personsQuery.data?.persons ?? []}
                value={personId}
                onChange={setPersonId}
              />
            )}
            <AffiliationRoleCheckboxes roles={roles} onChange={setRoles} idPrefix="org-aff" />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="affiliation-start-date">Start date (optional)</Label>
              <DatePicker
                id="affiliation-start-date"
                label="Start date"
                value={startDate}
                onChange={setStartDate}
              />
            </div>
          </div>
          <DialogFooter>
            {editing && (
              <Button
                type="button"
                variant="outline"
                className="mr-auto"
                disabled={endAffiliation.isPending || saveAffiliation.isPending}
                onClick={() => endAffiliation.mutate()}
              >
                End affiliation
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveAffiliation.isPending || endAffiliation.isPending}>
              {editing ? 'Save person' : 'Add person'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AffiliationPersonSelect({
  persons,
  value,
  onChange,
}: {
  persons: PersonOption[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="affiliation-person">Person</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="affiliation-person">
          <SelectValue placeholder="Select a person" />
        </SelectTrigger>
        <SelectContent>
          {persons.map((person) => (
            <SelectItem key={person.id} value={String(person.id)}>
              {person.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
